/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React from 'react';
import { Progress } from "../ui/progress";
import { Activity, Clock, Award, Target } from "lucide-react";
import { 
  getStudentStats,
  getCompletionMetrics
} from '../../utils/calculations';

const StatCard = ({ icon: Icon, label, value, subValue, variant = "default" }) => {
  const getVariantStyles = () => {
    const styles = {
      default: "text-blue-600 bg-blue-50",
      success: "text-green-600 bg-green-50",
      warning: "text-yellow-600 bg-yellow-50",
      danger: "text-red-600 bg-red-50"
    };
    return styles[variant] || styles.default;
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${getVariantStyles()}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-lg font-semibold">{value}</p>
            {subValue && (
              <p className="text-xs text-muted-foreground mt-1">{subValue}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentQuickStats = ({ student }) => {
  const stats = getStudentStats(student);
  const completion = getCompletionMetrics(student);
  
  const formatTime = (testResults) => {
    // Convert from seconds to appropriate units
    const totalSeconds = testResults?.reduce((sum, test) => sum + (test.time_spent || 0), 0) || 0;
    
    if (totalSeconds < 60) {
      // Less than a minute, show seconds only
      return `${totalSeconds}s`;
    } else if (totalSeconds < 3600) {
      // Less than an hour, show minutes and seconds
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = Math.round(totalSeconds % 60);
      return `${minutes}m ${seconds}s`;
    } else {
      // An hour or more, show hours, minutes and seconds
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = Math.round(totalSeconds % 60);
      return `${hours}h ${minutes}m ${seconds}s`;
    }
  };

  const getAttemptEfficiency = (testResults) => {
    if (!testResults?.length) return { value: 0, variant: 'default' };
    
    // Group attempts by test_id
    const testAttempts = {};
    testResults.forEach(result => {
      if (!testAttempts[result.test_id]) {
        testAttempts[result.test_id] = [];
      }
      testAttempts[result.test_id].push(result);
    });
    
    // Calculate attempts needed to pass each test
    const attemptsToPass = [];
    Object.values(testAttempts).forEach(attempts => {
      // Sort by attempt timestamp to get chronological order
      const sortedAttempts = attempts.sort((a, b) => 
        new Date(a.attempt_timestamp) - new Date(b.attempt_timestamp)
      );
      
      // Find first passing attempt using database passed field
      const firstPassIndex = sortedAttempts.findIndex(attempt =>
        attempt.passed === true
      );
      
      if (firstPassIndex >= 0) {
        // Count attempts needed to pass (including the passing attempt)
        attemptsToPass.push(firstPassIndex + 1);
      }
    });
    
    if (attemptsToPass.length === 0) {
      return { value: 0, variant: 'danger' };
    }
    
    // Calculate average attempts needed to pass
    const avgAttemptsToPass = attemptsToPass.reduce((sum, attempts) => sum + attempts, 0) / attemptsToPass.length;
    
    return {
      value: avgAttemptsToPass.toFixed(1),
      variant: avgAttemptsToPass <= 1.5 ? 'success' : 
              avgAttemptsToPass <= 2.5 ? 'warning' : 'danger'
    };
  };

  const getScoreVariant = (score) => {
    if (score >= 90) return "success";
    if (score >= 70) return "default";
    return "danger";
  };

  const efficiency = getAttemptEfficiency(student.test_results);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
       <StatCard
         icon={Activity}
         label="Average Score"
         value={`${stats.averageScore}%`}
         subValue={`Best: ${stats.bestScore}%`}
         variant={getScoreVariant(stats.averageScore)}
       />
       
       <StatCard
         icon={Target} 
         label="Learning Efficiency"
         value={`${efficiency.value} attempts`}
         subValue="Average attempts per pass"
         variant={efficiency.variant}
       />
       
       <StatCard
         icon={Clock}
         label="Total Time"
         value={formatTime(student.test_results)}
         subValue={`${stats.totalAttempts} total attempts`}
         variant="default"
       />
       
       <StatCard
         icon={Award}
         label="Completion"
         value={`${completion.completed}/${completion.total}`}
         subValue={`${completion.percentage}% complete`}
         variant={completion.completed === completion.total ? "success" : "default"}
       />
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium">Overall Progress</span>
          <span className="text-sm text-muted-foreground">
            {completion.completed} of {completion.total} tests passed
          </span>
        </div>
        <Progress 
          value={completion.percentage} 
          className="h-2"
        />
      </div>
    </div>
  );
};

export default StudentQuickStats;